/** Shared by the about-us band and the solutions deck, so the site never shows two "2,8M"s that disagree. */
import { formatNumber } from '@worldcoo/ui/lib/utils';

/**
 * `alwaysGroup`: Spanish (and Italian, normalized to it) sets `minimumGroupingDigits: 2`, so `Intl`
 * would print the four-digit points-of-sale figure as "5000".
 */
export function formatBandFigure(locale: string, value: number): string {
	return formatNumber(locale, value, 0, { alwaysGroup: true });
}

/**
 * One decimal, since flooring a live 2.763.110 to "2M" under-reports it by 0.76M; a whole number drops
 * it, so the BI-failure fallback of 2.000.000 reads "2M". Not `formatCompactNumber`: it prepends a `+`
 * and hardcodes the unit in English.
 */
export function formatMillions(locale: string, value: number): string {
	// Below a million the abbreviation would round to "0M".
	if (value < 1_000_000) return formatBandFigure(locale, value);

	const millions = Math.round(value / 100_000) / 10;
	return Number.isInteger(millions) ? `${formatNumber(locale, millions, 0)}M` : `${formatNumber(locale, millions, 1)}M`;
}

/**
 * "8,2M €", "517K €": a card has room for about six characters, not "517.482 €". Rounded like
 * `formatMillions`, or one total would read "8,2M €" and "8,1M €" in two sections.
 */
export function formatCompactMoney(locale: string, euros: number): string {
	if (euros >= 1_000_000) return `${formatMillions(locale, euros)} €`;
	if (euros >= 1_000) return `${formatBandFigure(locale, Math.round(euros / 1_000))}K €`;
	return `${formatBandFigure(locale, Math.round(euros))} €`;
}
